import { AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import i18n from "i18next";
import { FC, useEffect } from "react";

import { switchOrg } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { t } from "i18n/i18n";
import { ToastKeys } from "i18n/i18next";
import { ListItem } from "mds/components/ListItem";
import { BookSectionIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  courseSelectors,
  sectionSelectors,
  selectCurrentUserId,
  selectLinkableSectionIds,
  selectLinkableSectionIdsForTerm,
  selectLinkableTermIds,
  termSelectors,
} from "store/selectors";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import {
  toastLocalizedOperationCatcher,
  toastLocalizedOperationError,
  toastSuccessMessage,
} from "utils/alerts";
import { reportApiFailure } from "utils/api-errors";
import { getOrgIdFromCookie } from "utils/auth";
import { getCourseAndSectionDisplayTitle } from "utils/course";
import { useNavigate, useQueryParam } from "utils/routing";

const SectionLinkListItem: FC<{ sectionId: string }> = ({ sectionId }) => {
  const navigate = useNavigate();
  const lmsSectionId = useQueryParam("lmsSectionId");
  const section = useObjectSelector(
    (state) => sectionSelectors.selectById(state, sectionId),
    ["id", "title", "course_id", "lms_section_id"],
  );
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, section?.course_id),
    ["code", "title"],
  );

  if (!section || !course) {
    return null;
  }

  const isLinked = section.lms_section_id !== null;
  const fullTitle = getCourseAndSectionDisplayTitle(course, section);

  const onSelectSection = () => {
    if (!lmsSectionId || isLinked) return;

    storeApi.sections
      .partial_update(
        {
          id: sectionId,
          lms_section_id: lmsSectionId,
        },
        { skipToast: true },
      )
      .then(() => {
        toastSuccessMessage(t("success.toasts.connect_section_started_success"));
        navigate(`/redirect?sectionId=${sectionId}`);
      })
      .catch((error: AxiosError) => {
        // TODO: make a generic function for this
        const data = error.response?.data as Record<string, string>;

        if (data && "error" in data) {
          const dataError = data.error as ToastKeys;
          if (!i18n.exists(`error.toasts.${dataError}`)) {
            // If it doesn't exist, default to a generic error
            toastLocalizedOperationError("unknown");
          } else {
            toastLocalizedOperationError(dataError);
          }
        } else {
          toastLocalizedOperationCatcher("connect_lms_failure")(error);
        }
      });
  };

  return (
    <ListItem
      className="flex justify-between"
      // @ts-expect-error disabled is not typed
      disabled={isLinked}
      label={fullTitle}
      onClick={isLinked ? undefined : onSelectSection}
    >
      <div className="flex items-center gap-2">
        <BookSectionIcon />
        <span className={isLinked ? "text-black-tint-55" : ""}>{fullTitle}</span>
        {isLinked && (
          <span className="ml-2 text-black-tint-55">
            {t("link.connected_section", { lmsSectionId: section.lms_section_id })}
          </span>
        )}
      </div>
    </ListItem>
  );
};

const TermLinkSectionGroup: FC<{ termId: string }> = ({ termId }) => {
  const termTitle =
    useAppSelector((state) => termSelectors.selectById(state, termId)?.title) ?? "Unassigned";
  const sectionIds = useListSelector((state) => selectLinkableSectionIdsForTerm(state, termId));

  if (sectionIds.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="mb-2 text-lg font-bold">{termTitle}</h2>
      {sectionIds.map((sectionId) => (
        <SectionLinkListItem key={sectionId} sectionId={sectionId} />
      ))}
    </div>
  );
};

export default function Layout() {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const hasUnlinkedSections = useHasItemsSelector(selectLinkableSectionIds);
  const termIds = useListSelector(selectLinkableTermIds);

  const orgId = useQueryParam("orgId");
  const orgIdFromCookie = getOrgIdFromCookie();
  const lmsSectionId = useQueryParam("lmsSectionId");

  useEffect(() => {
    const maybeSwitchOrg = async () => {
      if (orgId && orgId !== orgIdFromCookie) {
        try {
          await switchOrg(orgId);
          window.location.reload();
        } catch (error) {
          reportApiFailure("Failed to auto-switch org", error);
        }
      }
    };
    maybeSwitchOrg();
  }, [orgIdFromCookie, orgId]);

  useEffect(() => {
    if (!currentUserId) return;

    storeApi.section_users.list({ user_id: currentUserId });
    storeApi.course_users.list({ user_id: currentUserId });
    storeApi.course_grouping_users.list({ user_id: currentUserId });
    storeApi.courses.list();
    storeApi.terms.list();
  }, [currentUserId]);

  if (!hasUnlinkedSections) {
    return (
      <LoadingScreen>
        <div className="flex flex-col gap-4 text-center">
          <div>{t("link.section_not_connected_1")}</div>
          <div>{t("link.section_not_connected_2")}</div>
        </div>
      </LoadingScreen>
    );
  }

  if (!lmsSectionId) {
    return <LoadingScreen>{t("link.no_sections_available")}</LoadingScreen>;
  }

  return (
    <div>
      {termIds.map((termId) => (
        <TermLinkSectionGroup key={termId} termId={termId} />
      ))}
    </div>
  );
}
